import { numeroPedido } from "../../lib/pedidosManuais";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { mensagemDeErro, novaChave, rpc } from "../../lib/rpc";
import { temPapel, useAuth } from "../../lib/auth";
import { supabase } from "../../lib/supabase";
import { sinalConcluido, sinalErro, sinalOk } from "../../lib/sinais";
import type { ModoSeparacao, ResumoOnda, Tarefa } from "../../lib/tipos";
import { fmtNum } from "../../lib/formato";
import { LeituraCodigo } from "../../componentes/LeituraCodigo";
import { AlertaErro, Confirmar } from "../../componentes/Alerta";
import { Botao, Carregando, Selo, Vazio } from "../../componentes/ui";
import { FaltasPedido, type FaltaPedido } from "../../componentes/FaltasPedido";

/** Pré-separação: sem onda, coleta no carrinho e coleta concluída (a separação na esteira é outra tela). */
export default function Separacao() {
  const [resumo, setResumo] = useState<ResumoOnda | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const r = await rpc<ResumoOnda>("wms_onda_atual");
      setResumo(r.ok ? r : null);
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  if (carregando) return <Carregando />;

  return (
    <>
      <AlertaErro mensagem={erro} onFechar={() => setErro(null)} />
      {!resumo && <SemOnda onPegou={setResumo} onErro={setErro} />}
      {resumo?.onda.status === "coletando" && <Coleta resumo={resumo} setResumo={setResumo} />}
      {resumo?.onda.status === "montando" && <ColetaConcluida resumo={resumo} onNova={() => setResumo(null)} />}
      {resumo && (resumo.onda.status === "concluida" || resumo.onda.status === "cancelada") && (
        <OndaConcluida resumo={resumo} onNova={() => setResumo(null)} />
      )}
    </>
  );
}

function CabecalhoOnda({ resumo }: { resumo: ResumoOnda }) {
  const { onda, contagem } = resumo;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-tinta px-3 py-2 text-white">
      <span className="font-sinal text-xl font-bold">Onda {onda.numero}</span>
      {onda.modo === "pedido"
        ? <span className="font-semibold">Pedido a pedido</span>
        : onda.tipo === "MONO_SKU" && onda.produto
          ? <span className="font-semibold">{onda.produto.sku ?? onda.produto.nome}</span>
          : <span className="font-semibold">Mista</span>}
      <span className="opacity-80">{contagem.pedidos} pedidos</span>
      {onda.carrinho && <span className="ml-auto rounded bg-white/15 px-2 py-0.5 font-sinal text-lg">{onda.carrinho}</span>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Abandonar a onda (qualquer etapa): pedidos voltam para a fila
// -----------------------------------------------------------------------------
const MOTIVOS_ABANDONO = ["Peguei a onda errada", "Vou separar outro pedido", "Fim do turno", "Problema no carrinho ou na posição"];

function AbandonarOnda({ resumo, setResumo }: { resumo: ResumoOnda; setResumo: (r: ResumoOnda) => void }) {
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [outro, setOutro] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const texto = motivo === "Outro" ? outro.trim() : motivo;

  async function abandonar() {
    setOcupado(true);
    setErro(null);
    try {
      const r = await rpc<ResumoOnda>("wms_onda_abandonar", { p_onda_id: resumo.onda.id, p_motivo: texto });
      if (r.ok) {
        setAberto(false);
        setResumo(r);
      } else setErro(r.mensagem ?? "Não foi possível abandonar");
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      <Botao variante="perigo" onClick={() => setAberto(true)} className="min-h-12">Abandonar onda</Botao>
      <Confirmar aberto={aberto} titulo={`Abandonar a onda ${resumo.onda.numero}?`} rotuloConfirmar="Abandonar" perigo ocupado={ocupado || texto.length < 3}
        onConfirmar={() => void abandonar()} onCancelar={() => setAberto(false)}>
        <p>Os pedidos não montados voltam para a fila e as reservas são liberadas. O que já está no carrinho precisa voltar para as posições.</p>
        <div className="mt-3 flex flex-col gap-2">
          {[...MOTIVOS_ABANDONO, "Outro"].map((m) => (
            <label key={m} className="flex min-h-11 items-center gap-2 rounded border-2 border-aco px-3">
              <input type="radio" name="motivo-abandono" checked={motivo === m} onChange={() => setMotivo(m)} className="h-5 w-5" /> {m}
            </label>
          ))}
          {motivo === "Outro" && (
            <input value={outro} onChange={(e) => setOutro(e.target.value)} placeholder="Escreva o motivo" autoFocus
              className="min-h-12 rounded-md border-2 border-tinta px-3" aria-label="Motivo" />
          )}
          {erro && <p className="font-semibold text-erro">{erro}</p>}
        </div>
      </Confirmar>
    </>
  );
}

// -----------------------------------------------------------------------------
// Sem onda: pegar a próxima
// -----------------------------------------------------------------------------
function SemOnda({ onPegou, onErro }: { onPegou: (r: ResumoOnda) => void; onErro: (m: string) => void }) {
  const { perfil } = useAuth();
  const lider = temPapel(perfil, "lider");
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const fila = useQuery({
    queryKey: ["fila-separacao"],
    queryFn: async () => {
      const { data } = await supabase.from("v_fila_separacao").select("*").order("prioridade_max", { ascending: false });
      return (data ?? []) as Array<{ tipo: string; sku: string | null; nome: string | null; pedidos: number }>;
    },
    refetchInterval: 20_000,
  });

  const modos = useQuery({
    queryKey: ["separacao-modos"],
    queryFn: async () => {
      const { data } = await supabase.from("configuracoes").select("valor").eq("chave", "separacao_modos").maybeSingle();
      const lista = (data?.valor ?? ["onda_sku", "onda_mista"]) as ModoSeparacao[];
      return lista.length ? lista : (["onda_sku", "onda_mista"] as ModoSeparacao[]);
    },
    staleTime: 60_000,
  });
  const liberados = modos.data ?? ["onda_sku", "onda_mista"];
  const temOnda = liberados.includes("onda_sku") || liberados.includes("onda_mista");

  async function pegar(modo: ModoSeparacao | null) {
    setOcupado(true);
    setAviso(null);
    try {
      const r = await rpc<ResumoOnda>("wms_onda_pegar_proxima", modo ? { p_modo: modo } : {});
      if (r.ok) {
        sinalOk();
        onPegou(r);
      } else {
        setAviso(r.mensagem ?? "Nenhuma onda disponível");
      }
    } catch (e) {
      onErro(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  const total = (fila.data ?? []).reduce((s, f) => s + f.pedidos, 0);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="font-sinal text-3xl font-bold">Pré-separação</h1>
      {temOnda && (
        <Botao grande onClick={() => void pegar(null)} disabled={ocupado} className="min-h-24 text-2xl">
          {ocupado ? "Montando a onda..." : "Pegar próxima onda"}
        </Botao>
      )}
      {(liberados.length > 1 || !temOnda) && (
        <div className="grid gap-3 sm:grid-cols-3">
          {liberados.includes("onda_sku") && liberados.length > 1 && (
            <Botao variante="secundario" onClick={() => void pegar("onda_sku")} disabled={ocupado} className="min-h-16 text-lg">
              Onda por SKU
            </Botao>
          )}
          {liberados.includes("onda_mista") && liberados.length > 1 && (
            <Botao variante="secundario" onClick={() => void pegar("onda_mista")} disabled={ocupado} className="min-h-16 text-lg">
              Onda mista
            </Botao>
          )}
          {liberados.includes("pedido") && (
            <Botao variante={temOnda ? "secundario" : "primario"} onClick={() => void pegar("pedido")} disabled={ocupado}
              className={temOnda ? "min-h-16 text-lg" : "min-h-24 text-2xl sm:col-span-3"}>
              {ocupado && !temOnda ? "Buscando..." : "Pegar um pedido"}
            </Botao>
          )}
        </div>
      )}
      {aviso && <p className="rounded-md bg-alerta-claro px-3 py-3 text-lg font-semibold text-alerta">{aviso}</p>}
      {lider && <PedidoEscolhido onPegou={onPegou} onErro={onErro} />}
      <section className="rounded-lg border-2 border-aco bg-chapa p-4">
        <h2 className="font-sinal text-xl font-semibold">Fila agora: {total} pedidos</h2>
        <ul className="mt-2 divide-y divide-aco">
          {(fila.data ?? []).slice(0, 8).map((f, i) => (
            <li key={i} className="flex items-center justify-between py-2">
              <span>{f.tipo === "MONO_SKU" ? (f.sku ?? f.nome) : "Pedidos mistos"}</span>
              <span className="font-sinal text-xl font-semibold">{f.pedidos}</span>
            </li>
          ))}
        </ul>
        {!fila.isLoading && total === 0 && <p className="mt-2 text-aco-escuro">Nenhum pedido aguardando pré-separação.</p>}
      </section>
    </div>
  );
}

/** Liderança: pré-separar um pedido escolhido da fila (onda de um pedido só). */
function PedidoEscolhido({ onPegou, onErro }: { onPegou: (r: ResumoOnda) => void; onErro: (m: string) => void }) {
  const [aberto, setAberto] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [faltas, setFaltas] = useState<FaltaPedido[]>([]);
  const fila = useQuery({
    queryKey: ["fila-pedidos-escolha"],
    enabled: aberto,
    queryFn: async () => {
      const { data } = await supabase.from("v_pedidos_lista").select("id, numero, origem, numero_manual, numero_ecommerce, canal, status, itens, tags")
        .in("status", ["aguardando_separacao", "aguardando_reposicao"]).order("prioridade", { ascending: false }).order("criado_em").limit(200);
      return (data ?? []) as Array<{ id: string; origem: string; numero_manual: number | null; numero: number | null; numero_ecommerce: string | null; canal: string | null; status: string; itens: string | null; tags: string[] }>;
    },
  });
  const visiveis = (fila.data ?? []).filter((p) => !filtro || `${numeroPedido(p)} ${p.numero_ecommerce ?? ""} ${p.itens ?? ""}`.toUpperCase().includes(filtro.toUpperCase()));

  async function separar(id: string) {
    setOcupado(true);
    setAviso(null);
    setFaltas([]);
    try {
      const r = await rpc<ResumoOnda & { faltas?: FaltaPedido[] }>("wms_onda_pegar_pedido", { p_pedido_id: id });
      if (r.ok) {
        sinalOk();
        onPegou(r);
      } else {
        setAviso(r.mensagem ?? "Não foi possível pré-separar este pedido");
        setFaltas(r.faltas ?? []);
      }
    } catch (e) {
      onErro(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  if (!aberto) {
    return <Botao variante="secundario" onClick={() => setAberto(true)} className="min-h-12">Pré-separar um pedido específico (líder)</Botao>;
  }
  return (
    <section className="rounded-lg border-2 border-tinta bg-chapa p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-sinal text-xl font-semibold">Escolher pedido da fila</h2>
        <Botao variante="fantasma" onClick={() => setAberto(false)}>Fechar</Botao>
      </div>
      <input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Número, e-commerce ou SKU" aria-label="Filtrar pedidos"
        className="mt-2 min-h-12 w-full rounded-md border-2 border-aco px-3" />
      {aviso && <p className="mt-2 rounded-md bg-alerta-claro px-3 py-2 font-semibold text-alerta">{aviso}</p>}
      {faltas.length > 0 && <div className="mt-2"><FaltasPedido faltas={faltas} lider onAtualizar={() => { setAviso(null); void fila.refetch(); }} /></div>}
      <ul className="mt-2 max-h-96 divide-y divide-aco overflow-y-auto">
        {visiveis.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-2 py-2">
            <span>
              <span className="font-sinal text-xl font-bold">Pedido {numeroPedido(p)}</span>{" "}
              {p.tags.map((t) => <Selo key={t} tom="faixa">{t}</Selo>)}
              {p.status === "aguardando_reposicao" && <Selo tom="alerta">sem saldo</Selo>}
              <span className="block text-sm text-aco-escuro">{p.itens} {p.canal && `· ${p.canal}`}</span>
            </span>
            <Botao disabled={ocupado} onClick={() => void separar(p.id)}>Pré-separar</Botao>
          </li>
        ))}
        {!fila.isLoading && !visiveis.length && <li className="py-2 text-aco-escuro">Nenhum pedido na fila.</li>}
      </ul>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Coleta no carrinho
// -----------------------------------------------------------------------------
type EtapaColeta = "posicao" | "produto" | "quantidade";

function Coleta({ resumo, setResumo }: { resumo: ResumoOnda; setResumo: (r: ResumoOnda) => void }) {
  const pendentes = useMemo(() => resumo.tarefas.filter((t) => t.status === "pendente"), [resumo.tarefas]);
  const [tarefaId, setTarefaId] = useState<string | null>(null);
  const tarefa: Tarefa | undefined = pendentes.find((t) => t.id === tarefaId) ?? pendentes[0];
  const [etapa, setEtapa] = useState<EtapaColeta>("posicao");
  const [posicaoLida, setPosicaoLida] = useState("");
  const [produtoLido, setProdutoLido] = useState("");
  const [qtd, setQtd] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [lampejo, setLampejo] = useState(0);
  const [tremor, setTremor] = useState(0);
  const [ocupado, setOcupado] = useState(false);
  const [verLista, setVerLista] = useState(false);
  const [confirmarFalta, setConfirmarFalta] = useState(false);

  const restante = tarefa ? tarefa.qtd_solicitada - tarefa.qtd_coletada : 0;
  const concluidas = resumo.tarefas.filter((t) => t.status !== "pendente").length;

  useEffect(() => {
    setEtapa("posicao");
    setPosicaoLida("");
    setProdutoLido("");
  }, [tarefa?.id]);

  function falhou(msg: string) {
    sinalErro();
    setTremor((n) => n + 1);
    setErro(msg);
  }

  async function vincularCarrinho(codigo: string) {
    setOcupado(true);
    try {
      const r = await rpc<ResumoOnda>("wms_onda_vincular_carrinho", { p_onda_id: resumo.onda.id, p_codigo_carrinho: codigo });
      if (r.ok) {
        sinalOk();
        setLampejo((n) => n + 1);
        setResumo(r);
      } else falhou(r.mensagem ?? "Carrinho inválido");
    } catch (e) {
      falhou(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  async function registrar(produto: string, quantidade: number) {
    if (!tarefa) return;
    setOcupado(true);
    try {
      const r = await rpc<ResumoOnda>("wms_coleta_registrar", {
        p_tarefa_id: tarefa.id, p_codigo_posicao: posicaoLida, p_codigo_produto: produto,
        p_quantidade: quantidade, p_idem: novaChave(),
      });
      if (r.ok) {
        sinalOk();
        setLampejo((n) => n + 1);
        setAviso(null);
        setTarefaId(null);
        setResumo(r);
        setEtapa("posicao");
      } else {
        falhou(r.mensagem ?? "Coleta recusada");
        if (r.codigo_erro === "POSICAO_ERRADA") setEtapa("posicao");
        if (r.codigo_erro === "PRODUTO_ERRADO" || r.codigo_erro === "CODIGO_DESCONHECIDO") setEtapa("produto");
      }
    } catch (e) {
      falhou(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  async function leitura(codigo: string) {
    if (!tarefa || ocupado) return;
    if (etapa === "posicao") {
      setOcupado(true);
      try {
        const r = await rpc("wms_coleta_bipar_posicao", { p_tarefa_id: tarefa.id, p_codigo: codigo, p_idem: novaChave() });
        if (r.ok) {
          sinalOk();
          setLampejo((n) => n + 1);
          setPosicaoLida(codigo);
          setEtapa("produto");
        } else falhou(r.mensagem ?? "Posição errada");
      } catch (e) {
        falhou(mensagemDeErro(e));
      } finally {
        setOcupado(false);
      }
      return;
    }
    if (etapa === "produto") {
      const { data: produtoId } = await supabase.rpc("wms_resolver_produto", { p_codigo: codigo });
      if (!produtoId) return falhou("Código não cadastrado");
      if (produtoId !== tarefa.produto_id) return falhou(`Produto errado. Esta posição pede ${tarefa.sku ?? tarefa.nome}`);
      setProdutoLido(codigo);
      if (restante === 1) {
        await registrar(codigo, 1);
      } else {
        sinalOk();
        setQtd(restante);
        setEtapa("quantidade");
      }
    }
  }

  async function registrarFalta() {
    if (!tarefa) return;
    setOcupado(true);
    try {
      const r = await rpc<ResumoOnda>("wms_coleta_falta", { p_tarefa_id: tarefa.id, p_idem: novaChave() });
      setConfirmarFalta(false);
      if (r.ok) {
        const realocado = Number(r.realocado ?? 0);
        const semSaldo = Number(r.sem_saldo ?? 0);
        setAviso(semSaldo > 0
          ? `Sem saldo em outra posição: ${fmtNum(semSaldo)} un de ${tarefa.sku} vão faltar na onda.`
          : realocado > 0 ? `${fmtNum(realocado)} un foram para outra posição da lista.` : null);
        setTarefaId(null);
        setResumo(r);
      } else falhou(r.mensagem ?? "Não foi possível registrar a falta");
    } catch (e) {
      falhou(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  if (!resumo.onda.carrinho) {
    return (
      <div className="flex flex-col gap-4">
        <AlertaErro mensagem={erro} onFechar={() => setErro(null)} />
        <CabecalhoOnda resumo={resumo} />
        <p className="text-lg">Pegue um carrinho vazio e bipe a etiqueta dele.</p>
        <LeituraCodigo rotulo="Bipe o carrinho" onLeitura={vincularCarrinho} desabilitado={ocupado} lampejo={lampejo} erro={tremor} />
        <AbandonarOnda resumo={resumo} setResumo={setResumo} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-4">
      <AlertaErro mensagem={erro} onFechar={() => setErro(null)} />
      <CabecalhoOnda resumo={resumo} />

      <div className="flex items-center gap-3">
        <div className="h-3 flex-1 overflow-hidden rounded-full bg-concreto-escuro" aria-hidden>
          <div className="h-full bg-ok transition-all" style={{ width: `${(concluidas / Math.max(1, resumo.tarefas.length)) * 100}%` }} />
        </div>
        <span className="font-sinal text-lg font-semibold">{concluidas} de {resumo.tarefas.length} posições</span>
      </div>

      {aviso && <p className="rounded-md bg-alerta-claro px-3 py-2 font-semibold text-alerta">{aviso}</p>}

      {tarefa && !verLista && (
        <section className="rounded-lg border-2 border-tinta bg-chapa">
          <div className={`px-4 pt-4 ${etapa === "posicao" ? "" : "opacity-60"}`}>
            <p className="text-sm font-semibold text-aco-escuro">Vá até a posição</p>
            <p className="font-sinal text-7xl font-bold leading-none tracking-tight sm:text-8xl">{tarefa.local}</p>
          </div>
          <div className="mt-4 flex items-end justify-between gap-3 border-t-2 border-aco px-4 py-4">
            <div>
              <p className="text-2xl font-semibold leading-tight">{tarefa.nome}</p>
              <p className="text-lg text-aco-escuro">{tarefa.sku}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-aco-escuro">Pegue</p>
              <p className="font-sinal text-6xl font-bold leading-none">{fmtNum(restante)}</p>
            </div>
          </div>
        </section>
      )}

      {tarefa && !verLista && etapa !== "quantidade" && (
        <LeituraCodigo
          rotulo={etapa === "posicao" ? "Bipe a posição" : "Bipe o produto"}
          onLeitura={leitura} desabilitado={ocupado} lampejo={lampejo} erro={tremor}
        />
      )}

      {tarefa && !verLista && etapa === "quantidade" && (
        <section data-dialogo-aberto className="rounded-lg bg-faixa p-4">
          <p className="font-sinal text-2xl font-bold">Quantas unidades você pegou?</p>
          <div className="mt-3 flex items-center gap-3">
            <button type="button" onClick={() => setQtd((q) => Math.max(1, q - 1))}
              className="min-h-16 w-16 rounded-md border-2 border-tinta bg-chapa text-3xl font-bold" aria-label="Menos um">-</button>
            <input type="number" inputMode="numeric" min={1} max={restante} value={qtd}
              onChange={(e) => setQtd(Math.max(0, Math.min(restante, Number(e.target.value))))}
              className="min-h-16 w-full rounded-md border-2 border-tinta bg-chapa text-center font-sinal text-4xl font-bold" aria-label="Quantidade" />
            <button type="button" onClick={() => setQtd((q) => Math.min(restante, q + 1))}
              className="min-h-16 w-16 rounded-md border-2 border-tinta bg-chapa text-3xl font-bold" aria-label="Mais um">+</button>
          </div>
          <div className="mt-3 flex gap-2">
            <Botao variante="secundario" onClick={() => setEtapa("produto")}>Voltar</Botao>
            <Botao variante="confirmar" grande className="flex-1" disabled={ocupado || qtd < 1}
              onClick={() => void registrar(produtoLido, qtd)}>
              Confirmar {fmtNum(qtd)}
            </Botao>
          </div>
          {qtd < restante && <p className="mt-2 font-semibold">Faltam {fmtNum(restante - qtd)} nesta posição depois desta coleta.</p>}
        </section>
      )}

      {verLista && (
        <section className="rounded-lg border-2 border-aco bg-chapa">
          <ul className="divide-y divide-aco">
            {resumo.tarefas.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2 px-3 py-2">
                <div>
                  <p className="font-sinal text-xl font-bold">{t.local}</p>
                  <p className="text-sm">{t.sku} {t.nome}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-sinal text-lg">{fmtNum(t.qtd_coletada)}/{fmtNum(t.qtd_solicitada)}</span>
                  {t.status === "pendente"
                    ? <Botao variante="secundario" onClick={() => { setTarefaId(t.id); setVerLista(false); }}>Ir</Botao>
                    : <Selo tom={t.status === "falta" ? "alerta" : "ok"}>{t.status === "falta" ? "Falta" : "Ok"}</Selo>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        <Botao variante="secundario" onClick={() => setVerLista((v) => !v)}>{verLista ? "Voltar à coleta" : "Ver lista"}</Botao>
        {tarefa && !verLista && <Botao variante="perigo" onClick={() => setConfirmarFalta(true)}>Falta nesta posição</Botao>}
        <span className="ml-auto"><AbandonarOnda resumo={resumo} setResumo={setResumo} /></span>
      </div>

      <Confirmar aberto={confirmarFalta} titulo="Registrar falta" rotuloConfirmar="Registrar falta" perigo ocupado={ocupado}
        onConfirmar={() => void registrarFalta()} onCancelar={() => setConfirmarFalta(false)}>
        {tarefa && <>Faltam <strong>{fmtNum(restante)} un</strong> de {tarefa.sku} em <strong>{tarefa.local}</strong>. O sistema procura outra posição e pede inventário desta.</>}
      </Confirmar>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Pré-separação concluída: a parte de quem coletou terminou. A separação pedido
// por pedido é na esteira, feita por outra pessoa (tela Separação).
// -----------------------------------------------------------------------------
function ColetaConcluida({ resumo, onNova }: { resumo: ResumoOnda; onNova: () => void }) {
  const pedidos = resumo.pedidos.filter((p) => p.ativo && !p.falta).length;
  const faltas = resumo.pedidos.filter((p) => p.ativo && p.falta).length;
  useEffect(() => { sinalConcluido(); }, []);
  return (
    <div className="flex flex-col gap-4">
      <CabecalhoOnda resumo={resumo} />
      <p className="rounded-lg bg-ok px-4 py-4 font-sinal text-3xl font-bold text-white">Pré-separação concluída</p>
      <section className="rounded-lg border-2 border-tinta bg-chapa p-4">
        <p className="text-xl">Leve o carrinho <strong className="font-sinal text-3xl">{resumo.onda.carrinho}</strong> para a esteira.</p>
        <p className="mt-1 text-aco-escuro">{pedidos} pedido(s){faltas ? `, ${faltas} com falta` : ""}. Sua parte terminou.</p>
        {resumo.carrinho_itens.length > 0 && (
          <ul className="mt-2 grid grid-cols-2 gap-x-4 text-sm">
            {resumo.carrinho_itens.map((i) => <li key={i.produto_id}>{i.nome}: <strong>{fmtNum(i.quantidade)}</strong></li>)}
          </ul>
        )}
      </section>
      <Botao grande onClick={onNova} className="min-h-20 text-2xl">Pegar próxima onda</Botao>
      <Link to="/" className="text-center font-semibold underline">Voltar ao início</Link>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Onda concluída
// -----------------------------------------------------------------------------
function OndaConcluida({ resumo, onNova }: { resumo: ResumoOnda; onNova: () => void }) {
  const navegar = useNavigate();
  const sobras = resumo.carrinho_itens;
  return (
    <div className="flex flex-col gap-4">
      <CabecalhoOnda resumo={resumo} />
      {resumo.onda.status === "cancelada" || resumo.abandonada ? (
        <p className="rounded-lg bg-alerta px-4 py-4 font-sinal text-3xl font-bold text-white">
          Onda abandonada: os pedidos não montados voltaram para a fila
        </p>
      ) : (
        <p className="rounded-lg bg-ok px-4 py-4 font-sinal text-3xl font-bold text-white">
          Onda concluída: {resumo.contagem.pedidos_montados} pedidos nas caixas
        </p>
      )}
      {sobras.length > 0 ? (
        <section className="rounded-lg border-2 border-alerta bg-alerta-claro p-4">
          <h2 className="font-sinal text-2xl font-bold text-alerta">Sobrou no carrinho {resumo.onda.carrinho}</h2>
          <ul className="mt-2">{sobras.map((s) => <li key={s.produto_id} className="text-lg">{s.nome} ({s.sku}): <strong>{fmtNum(s.quantidade)}</strong></li>)}</ul>
          <Botao grande className="mt-3 w-full" onClick={() => navegar(`/estoque/transferir?origem=${encodeURIComponent(resumo.onda.carrinho ?? "")}`)}>
            Devolver sobras às posições
          </Botao>
        </section>
      ) : (
        <Vazio>Carrinho vazio. Pode levar para a área de carrinhos.</Vazio>
      )}
      <Botao grande variante={sobras.length ? "secundario" : "primario"} onClick={onNova}>Pegar próxima onda</Botao>
      <Link to="/" className="text-center font-semibold underline">Voltar ao início</Link>
    </div>
  );
}
